/**
 * Client side of the server-owned meta-game (design D1/D3, port addition): the texts and the small pure helpers the
 * game needs around FWP.net_command. Meta-game operations (buy, sell, craft, claims, settings, ...) run on the backend
 * as `player.command` ops; the game adopts the canonical state the server returns and shows the op's result.
 *
 * As everywhere in the game, the Russian text is the source and the translation key (src/game/runtime/i18n.ts); every
 * literal here has a catalogue entry (tools/i18n/extra.json).
 */
import * as as3 from './as3';
import { isAllowedNameChar } from '../rules/ops/progress-profile';

// ---------------------------------------------------------------- messages

/** A refused command whose reason has no text of its own. */
export const MSG_COMMAND_REFUSED = 'Сервер отклонил действие. Загружены актуальные данные.';

/** Texts of the refusal reasons (VALIDATION_FAILED `reason`) a player can meet in practice. */
const REFUSAL_TEXTS: Readonly<Record<string, string>> = {
  not_enough_gold: 'Недостаточно золота.',
  not_enough_diam: 'Недостаточно алмазов.',
  // The annihilator auction (auction.buy, design D7): somebody bought first, or the player holds it already.
  price_changed: 'Цена изменилась: Аннигилятор уже купили. Проверьте новую цену.',
  already_holder: 'Аннигилятор и так ваш.',
};

/** Short message after the server refused a command (the game has re-loaded the server's state by then). */
export function commandRefusedText(reason: string): string {
  return Object.prototype.hasOwnProperty.call(REFUSAL_TEXTS, reason) ? REFUSAL_TEXTS[reason] : MSG_COMMAND_REFUSED;
}

/** "Gold collected: N" after the tribute of the captured zones (tribute.claim). */
export function tributeCollectedText(gold: number): string {
  return 'Собрано золота: ' + gold;
}

// ---------------------------------------------------------------- op results -> game memory

/** The level-up part of a claim's result (achiev.claim / task.claim: src/game/rules ops/progress-common LevelUpResult). */
export interface LevelUpResult {
  levels: number;
  skill_pts: number;
  perk_pts: number;
  diam: number;
  surpriseWeapons: string[];
}

/**
 * FWP.end_game after a claim as the original built it (new_interf 'get_achiev_bonus' / 'get_task_bonus': a neutral
 * end-game object, then check_new_level filled the level-up fields), from the level-up the server applied. It feeds
 * init_new_level (the new level / surprise screens).
 */
export function claimEndGame(levelUp: LevelUpResult | null | undefined): any {
  const lu = levelUp || { levels: 0, skill_pts: 0, perk_pts: 0, diam: 0, surpriseWeapons: [] };
  const surprise: any[] = [lu.surpriseWeapons.length, ""];
  for (let i = 0; i < lu.surpriseWeapons.length; i++) {
    surprise[i + 1] = lu.surpriseWeapons[i];
  }
  return {
    "win": false,
    "exp_bonus": 0,
    "gold_bonus": 0,
    "rating_bonus": 0,
    "levels_upg": lu.levels,
    "diam_bonus": lu.diam,
    "skill_pts": lu.skill_pts,
    "perk_pts": lu.perk_pts,
    "surprise_weapons": surprise,
    "game_stage": 0,
    "game_mode": "",
    "graves": [0, {}]
  };
}

/**
 * skills.commit arguments of one session of the skill/perk editor ('save' of the skills screen): the target values of
 * FWP.temp_player that differ from the player (attack, protect, health, worms), the perks added or upgraded in the
 * order the editor added them (the order of temp_player.perks), and the perk slot purchases in the order they were
 * made. The server recomputes every cost from its own state.
 */
export function skillsCommitArgs(player: any, temp: any, slotBuys: readonly string[]): any {
  const args: any = {};
  for (const k of ["attack", "protect", "health", "worms"]) {
    if (temp[k].value != player[k].value) {
      args[k] = temp[k].value;
    }
  }
  const perks: any = {};
  let changed = false;
  for (const k of as3.keys(temp.perks)) {
    if (temp.perks[k] != (player.perks[k] || 0)) {
      perks[k] = temp.perks[k];
      changed = true;
    }
  }
  if (changed) {
    args.perks = perks;
  }
  if (slotBuys.length > 0) {
    args.slots = slotBuys.slice();
  }
  return args;
}

/**
 * The texts of the settings screen's editable worm name fields as the player left them. Render fills field n with the
 * stored name or, when there is none, the default `defaultName + n` ('Червь #<n>'), and the field shows it through
 * the active language (`display`, the text fields' filter as3.tr), so reading a field back gives the translation. A
 * field that still shows the name render put there keeps that name (what the original saved for an untouched field,
 * translated again wherever it is shown); a field showing the default name's translation gives the default name; any
 * other text is the player's own.
 */
export function wormNameTexts(fields: readonly string[], stored: readonly any[], defaultName: string, display: (text: string) => string): string[] {
  const out: string[] = [];
  for (let i = 0; i < fields.length; i++) {
    const text = String(fields[i] == null ? "" : fields[i]);
    const byDefault = defaultName + (i + 1);
    const shown = stored[i] ? String(stored[i]) : byDefault;
    out.push(text == display(shown) ? shown : text == display(byDefault) ? byDefault : text);
  }
  return out;
}

/**
 * The names of the settings screen's worm name fields the player may edit (worms + 2, render 3158-3175) as
 * names.set sends them: filtered like the input fields do (check_tf: isAllowedNameChar, the rule names.set checks)
 * and cut to the 15 characters the backend keeps.
 */
export function wormNamesArg(texts: readonly string[]): string[] {
  const out: string[] = [];
  for (const t of texts) {
    let s = "";
    const text = String(t == null ? "" : t);
    for (let i = 0; i < text.length; i++) {
      if (isAllowedNameChar(text.charCodeAt(i))) {
        s += text.charAt(i);
      }
    }
    out.push(s.slice(0, 15));
  }
  return out;
}
